import { useEventListener } from 'CustomHooks/useEventListener';
import Konva from 'konva';
import React, { RefObject, useCallback, useRef, useState } from 'react';
import { GetDisplayMode, GetRelativePointerPosition } from 'utils/Global';
import { Rectangle } from './constants';
import { snfCanvasPadding } from 'components/searchAndFilter/canvas/constants';
import useRMLite from 'CustomHooks/useRMLite';
import { DisplayMode } from 'components/Experiment/constants';

const scaleBy = 1.05;
type CanvasZoomAndScrollReturnType = [
  handleZoomAndScroll: (e: any) => void,
  handleZoomAndScrollToFocusArea: (focusArea: Rectangle) => void,
  scale: number,
  setScale: React.Dispatch<React.SetStateAction<number>>,
];

const minZoomScale = 1;
export const useCanvasZoomAndScroll = (
  _stage,
  containerNode,
  initialZoomValue: number,
): CanvasZoomAndScrollReturnType => {
  const [scale, setScale] = useState(initialZoomValue);

  const handleZoomAndScrollToFocusArea = useCallback(
    (focusArea: Rectangle) => {
      const canvasWidth = _stage.width() - snfCanvasPadding;
      const canvasHeight = _stage.height() - snfCanvasPadding;

      const scaleX = canvasWidth / focusArea.width;
      const scaleY = canvasHeight / focusArea.height;
      const newScale = Math.min(scaleX, scaleY);
      const newPosX =
        -focusArea.x * newScale + (canvasWidth - focusArea.width * newScale) / 2;
      const newPosY =
        -focusArea.y * newScale + (canvasHeight - focusArea.height * newScale) / 2;

      _stage.to({
        scaleX: newScale,
        scaleY: newScale,
        x: newPosX,
        y: newPosY,
        duration: 0,
        onFinish: () => {
          const container = containerNode;
          const scrollLeft = -newPosX - (container.clientWidth - canvasWidth) / 2;
          const scrollTop = -newPosY - (container.clientHeight - canvasHeight) / 2;

          container.scrollLeft = scrollLeft;
          container.scrollTop = scrollTop;
        },
      });

      setScale(newScale);
    },
    [containerNode, _stage, scale],
  );

  const handleZoomAndScroll = useCallback(
    (e) => {
      let newScale = scale;
      if (e?.evt) {
        e.evt.preventDefault();
        newScale = e.evt.deltaY > 0 ? scale / scaleBy : scale * scaleBy;
      } else {
        if (e === 0) {
          newScale = minZoomScale;
        } else {
          newScale = e < 0 ? scale / scaleBy : scale * scaleBy;
        }
      }

      if (!containerNode || !_stage) {
        return;
      }
      const pointerPos = _stage.getPointerPosition();
      const oldScale = _stage.scaleX();

      if (newScale < minZoomScale && scale > newScale) {
        return;
      }

      setScale(newScale);

      const containerWidth = containerNode.clientWidth;
      const containerHeight = containerNode.clientHeight;
      const visibleCenterInContainerCoords = {
        x: snfCanvasPadding + containerWidth / 2,
        y: snfCanvasPadding + containerHeight / 2,
      };

      const pointToFocus = e?.evt ? pointerPos : visibleCenterInContainerCoords;
      const mousePointTo = {
        x: pointToFocus.x / oldScale - _stage.x() / oldScale,
        y: pointToFocus.y / oldScale - _stage.y() / oldScale,
      };

      const newPos = {
        x: -(mousePointTo.x - pointToFocus.x / newScale) * newScale,
        y: -(mousePointTo.y - pointToFocus.y / newScale) * newScale,
      };

      _stage.scale({ x: newScale, y: newScale });
      _stage.position(newPos);
      _stage.batchDraw();

      if (e === 0) {
        containerNode.scrollLeft = snfCanvasPadding;
        containerNode.scrollTop = snfCanvasPadding;
        return;
      }

      const desiredScrollLeft = snfCanvasPadding - newPos.x;
      const desiredScrollTop = snfCanvasPadding - newPos.y;

      const maxScrollLeft = containerNode.scrollWidth - containerWidth;
      const maxScrollTop = containerNode.scrollHeight - containerHeight;

      containerNode.scrollLeft = Math.max(0, Math.min(desiredScrollLeft, maxScrollLeft));
      containerNode.scrollTop = Math.max(0, Math.min(desiredScrollTop, maxScrollTop));
    },
    [containerNode, _stage, scale],
  );

  return [handleZoomAndScroll, handleZoomAndScrollToFocusArea, scale, setScale];
};

type ZoomRect = { x: number; y: number; width: number; height: number };

export function useCanvasMagnifier(
  stageRef: React.RefObject<Konva.Stage>,
  onZoomAreaSelected?: (rect: ZoomRect) => void,
) {
  const [selectionRectangle, setSelectionRectangle] = useState<ZoomRect | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const hasDrawnRef = useRef<boolean>(false);
  const isRMLite = useRMLite();
  const displayMode = GetDisplayMode();
  const enableNativeMenu = isRMLite && displayMode === DisplayMode.WELLIMAGE;

  useEventListener('contextmenu', (e) => {
    if (hasDrawnRef.current || !enableNativeMenu) {
      e.preventDefault();
    }
  });

  const onMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.evt.button === 2 && stageRef.current) {
        e.evt.preventDefault();
        const pos = GetRelativePointerPosition(stageRef.current);
        setStartPos(pos);
        setSelectionRectangle({ x: pos.x, y: pos.y, width: 0, height: 0 });
        setIsActive(true);
        hasDrawnRef.current = false;
      }
    },
    [stageRef],
  );

  const onMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!isActive || (!stageRef.current && selectionRectangle)) {
        return;
      }
      const pos = GetRelativePointerPosition(stageRef.current);
      const newRect = {
        x: Math.min(startPos.x, pos.x),
        y: Math.min(startPos.y, pos.y),
        width: Math.abs(pos.x - startPos.x),
        height: Math.abs(pos.y - startPos.y),
      };

      if (newRect.width > 1 && newRect.height > 1) {
        hasDrawnRef.current = true;
      }

      setSelectionRectangle(newRect);
    },
    [isActive, startPos, stageRef],
  );

  const onMouseUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (isActive && e.evt.button === 2) {
        if (
          selectionRectangle &&
          selectionRectangle.width > 0 &&
          selectionRectangle.height > 0 &&
          onZoomAreaSelected
        ) {
          onZoomAreaSelected(selectionRectangle);
        }
        setSelectionRectangle(null);
        setIsActive(false);
        return selectionRectangle?.height && selectionRectangle?.width;
      }
    },
    [isActive, selectionRectangle, onZoomAreaSelected],
  );

  return {
    selectionRectangle:
      selectionRectangle?.width && selectionRectangle?.height ? selectionRectangle : null,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  };
}

export function useCanvasPanCursor(
  stageRef: React.RefObject<Konva.Stage>,
  containerRef: React.RefObject<HTMLElement>,
  isPanActive: boolean,
) {
  const [isDragging, setIsDragging] = useState(false);

  const setCursor = useCallback(
    (cursor: string) => {
      if (containerRef.current) {
        containerRef.current.style.cursor = cursor;
      }
      if (stageRef.current?.container()) {
        stageRef.current.container().style.cursor = cursor;
      }
    },
    [containerRef, stageRef],
  );

  const startDragging = useCallback(() => {
    setIsDragging(true);
    setCursor('grabbing');
  }, [setCursor]);

  const stopDragging = useCallback(() => {
    setIsDragging(false);
    setCursor(isPanActive ? 'grab' : 'inherit');
  }, [isPanActive, setCursor]);

  const onDragStart = useCallback(() => {
    startDragging();
  }, [startDragging]);

  const onDragMove = useCallback(() => {
    if (!isDragging) {
      setIsDragging(true);
    }
    setCursor('grabbing');
  }, [isDragging, setCursor]);

  const onDragEnd = useCallback(() => {
    stopDragging();
  }, [stopDragging]);

  const onMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if ((isPanActive && e.evt.button === 0) || e.evt.button === 1) {
        startDragging();
      }
    },
    [isPanActive, startDragging],
  );

  const onMouseUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.evt.button === 0 || e.evt.button === 1) {
        stopDragging();
      }
    },
    [stopDragging],
  );

  setCursor(isPanActive ? 'grab' : 'inherit');
  return {
    isDragging,
    onDragStart,
    onDragMove,
    onDragEnd,
    onMouseDown,
    onMouseUp,
  };
}

export const useRMLiteSaveImageOnRightClick = (
  stageRef: RefObject<Konva.Stage>,
  imageLayerRef: RefObject<Konva.Layer>,
  scaleLayerRef: RefObject<Konva.Layer>,
) => {
  const isRMLite = useRMLite();
  const injectImage = useCallback(() => {
    if (!isRMLite) {
      return;
    }

    const stage = stageRef.current;
    if (!stage) {
      return;
    }

    const imageNode = imageLayerRef.current?.findOne(
      '#wellimage-group-image',
    ) as Konva.Image;
    if (!imageNode) {
      return;
    }

    const prevScale = stage.scaleX();
    const prevX = stage.x();
    const prevY = stage.y();

    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.draw();

    // an overlay canvas combining layers (image, scalebar) to create one element to be saved
    // this is to handle Konva that creates canvas element per layer and browser only saves top layer

    requestAnimationFrame(() => {
      const imageX = imageNode.x() ?? 0;
      const imageWidth = imageNode.width();
      const imageHeight = imageNode.height();
      const dpr = window.devicePixelRatio || 1;

      const saveImage = document.createElement('canvas');
      saveImage.width = imageWidth * dpr;
      saveImage.height = imageHeight * dpr;
      const ctx = saveImage.getContext('2d')!;
      ctx.scale(dpr, dpr);

      const imageCanvas = imageLayerRef.current?.getCanvas()._canvas;
      if (imageCanvas) {
        ctx.drawImage(
          imageCanvas,
          Math.ceil(imageX * dpr),
          0,
          imageWidth * dpr,
          imageHeight * dpr,
          0,
          0,
          imageWidth,
          imageHeight,
        );
      }

      const scaleLayer = scaleLayerRef.current;
      if (scaleLayer) {
        const wasVisible = scaleLayer.visible();
        scaleLayer.visible(true);
        const freshCanvas = scaleLayer.toCanvas();
        ctx.drawImage(freshCanvas, 0, 0);
        scaleLayer.visible(wasVisible);
      }

      stage.scale({ x: prevScale, y: prevScale });
      stage.position({ x: prevX, y: prevY });
      stage.draw();

      saveImage.style.cssText = `
        position: absolute;
        top: 0;
         left: ${Math.ceil(imageX)}px;
        width: ${imageWidth}px;
        height: ${imageHeight}px;
        z-index: 9999;
        opacity: 0;
      `;

      const container = stage.container();
      container.style.position = 'relative';
      container.appendChild(saveImage);

      const cleanup = () => {
        saveImage.remove();
        document.removeEventListener('mousedown', cleanup);
        document.removeEventListener('keydown', cleanup);
      };
      setTimeout(() => document.addEventListener('mousedown', cleanup), 100);
      document.addEventListener('keydown', cleanup);
    });
  }, [isRMLite, stageRef, imageLayerRef, scaleLayerRef]);

  return injectImage;
};
